import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Chip from "@material-ui/core/Chip";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ContactDrawer from "../../components/ContactDrawer";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  mainContainer: {
    position: "relative",
    overflow: "hidden",
  }
}));

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);

  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [availableServices, setAvailableServices] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContact, setDrawerContact] = useState({});

  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp");
        setWhatsapps(data || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetchWhatsapps();
  }, []);

  useEffect(() => {
    const fetchServicesAndStatuses = async () => {
      try {
        const { data } = await api.get("/settings");
        const wappId = selectedWhatsappId || 1;
        const servSetting = data.find(s => s.key === `services_list_wapp_${wappId}`) || data.find(s => s.key === "services_list");
        if (servSetting && servSetting.value) {
          setAvailableServices(JSON.parse(servSetting.value));
        } else {
          setAvailableServices([]);
        }

        const statSetting = data.find(s => s.key === `statuses_list_wapp_${wappId}`) || data.find(s => s.key === "statuses_list");
        if (statSetting && statSetting.value) {
          setAvailableStatuses(JSON.parse(statSetting.value));
        } else {
          setAvailableStatuses([]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    setSelectedServices([]);
    setSelectedStatuses([]);
    fetchServicesAndStatuses();
  }, [selectedWhatsappId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, selectedWhatsappId, selectedServices, selectedStatuses]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: {
              searchParam,
              pageNumber,
              whatsappId: selectedWhatsappId,
              services: selectedServices.join(","),
              statuses: selectedStatuses.join(",")
            },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, selectedWhatsappId, selectedServices, selectedStatuses]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("contact", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
        if (drawerContact.id === data.contact?.id) {
          setDrawerContact(data.contact);
        }
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [drawerContact]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleSaveTicket = async (contactId, whatsappId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        userId: user?.id,
        status: "open",
        whatsappId,
      });
      history.push(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleOpenDrawer = (contact) => {
    setDrawerContact(contact);
    setDrawerOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer className={classes.mainContainer} id="drawer-container">
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={() => setDrawerOpen(false)}
        contact={drawerContact}
        loading={false}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : `${i18n.t("contacts.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={(e) =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleimportContact()
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <MainHeader>
        <Title>{i18n.t("contacts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => setConfirmOpen(true)}
          >
            {i18n.t("contacts.buttons.import")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenContactModal}
          >
            {i18n.t("contacts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper variant="outlined" style={{ padding: "12px 16px", margin: "8px 8px 12px 8px", backgroundColor: "#f8fafc", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
        {/* 1. SELETOR DE MARCA / CONEXÃO */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Typography style={{ fontWeight: "bold", fontSize: "0.88rem", color: "#0f172a" }}>
            📱 Marca:
          </Typography>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200, backgroundColor: "#ffffff" }}>
            <Select
              value={selectedWhatsappId}
              onChange={(e) => setSelectedWhatsappId(e.target.value)}
              style={{ fontWeight: "bold", color: "#0284c7" }}
            >
              <MenuItem value="">🌐 TODAS AS MARCAS</MenuItem>
              {whatsapps.map(w => (
                <MenuItem key={w.id} value={w.id.toString()}>
                  📱 {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* 2. SELETOR DE SERVIÇOS (MULTI-SELECT) */}
        {selectedWhatsappId && availableServices.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography style={{ fontWeight: "bold", fontSize: "0.88rem", color: "#0f172a" }}>
              🩺 Serviços:
            </Typography>
            <FormControl variant="outlined" size="small" style={{ minWidth: 220, backgroundColor: "#ffffff" }}>
              <Select
                multiple
                value={selectedServices}
                onChange={(e) => setSelectedServices(e.target.value)}
                renderValue={(selected) => (
                  selected.length === 0 ? <span style={{ color: "#94a3b8" }}>Todos os Serviços</span> :
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {selected.map((val) => (
                      <Chip key={val} label={val} size="small" style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontWeight: "bold", height: 22 }} />
                    ))}
                  </div>
                )}
                displayEmpty
              >
                {availableServices.map((serv) => (
                  <MenuItem key={serv.id || serv.name} value={serv.name}>
                    <Checkbox checked={selectedServices.indexOf(serv.name) > -1} size="small" color="primary" />
                    <ListItemText primary={serv.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        {/* 3. SELETOR DE STATUS (MULTI-SELECT) */}
        {selectedWhatsappId && availableStatuses.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography style={{ fontWeight: "bold", fontSize: "0.88rem", color: "#0f172a" }}>
              📊 Status:
            </Typography>
            <FormControl variant="outlined" size="small" style={{ minWidth: 220, backgroundColor: "#ffffff" }}>
              <Select
                multiple
                value={selectedStatuses}
                onChange={(e) => setSelectedStatuses(e.target.value)}
                renderValue={(selected) => (
                  selected.length === 0 ? <span style={{ color: "#94a3b8" }}>Todos os Status</span> :
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {selected.map((val) => (
                      <Chip key={val} label={val} size="small" style={{ backgroundColor: "#f1f5f9", color: "#0f172a", fontWeight: "bold", height: 22 }} />
                    ))}
                  </div>
                )}
                displayEmpty
              >
                {availableStatuses.map((stat) => (
                  <MenuItem key={stat.id || stat.name} value={stat.name}>
                    <Checkbox checked={selectedStatuses.indexOf(stat.name) > -1} size="small" color="primary" />
                    <ListItemText primary={stat.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
      </Paper>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell style={{ fontWeight: "bold" }}>{i18n.t("contacts.table.name")}</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>Conexão / Marca</TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>
                {i18n.t("contacts.table.whatsapp")}
              </TableCell>
              <TableCell align="center" style={{ fontWeight: "bold" }}>
                {i18n.t("contacts.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id} hover>
                  <TableCell style={{ paddingRight: 0 }}>
                    {<Avatar src={contact.profilePicUrl} />}
                  </TableCell>
                  <TableCell style={{ fontWeight: 500 }}>{contact.name}</TableCell>
                  <TableCell align="center">
                    <span style={{ fontSize: "0.78rem", backgroundColor: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0", borderRadius: 6, padding: "3px 10px", fontWeight: "bold" }}>
                      📱 {contact.whatsapp?.name || "Prime Nutra"}
                    </span>
                  </TableCell>
                  <TableCell align="center" style={{ fontFamily: "monospace", fontSize: "0.9rem" }}>{contact.number}</TableCell>
                  <TableCell align="center">
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                      <IconButton
                        size="medium"
                        title="Ficha Completa & Timeline"
                        onClick={() => handleOpenDrawer(contact)}
                        style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}
                      >
                        <AssignmentIndIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="medium"
                        title="Abrir Chat no WhatsApp"
                        onClick={() => handleSaveTicket(contact.id, contact.whatsappId)}
                        style={{ backgroundColor: "#dcfce7", color: "#16a34a" }}
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="medium"
                        title="Editar Contato"
                        onClick={() => hadleEditContact(contact.id)}
                        style={{ backgroundColor: "#f1f5f9", color: "#475569" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <Can
                        role={user.profile}
                        perform="contacts-page:deleteContact"
                        yes={() => (
                          <IconButton
                            size="medium"
                            title="Excluir Contato"
                            onClick={(e) => {
                              setConfirmOpen(true);
                              setDeletingContact(contact);
                            }}
                            style={{ backgroundColor: "#fee2e2", color: "#dc2626" }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
